import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { calculateDeliveryFee } from "../utils/deliveryFee";

const useDeliveryFee = (items) => {
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [loading, setLoading] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const latest = useRef({ key: null, promise: null });
  const key = items.map((item) => `${item.id}:${item.quantity}`).join(",");

  const loadDeliveryFee = useCallback(() => {
    if (latest.current.key === key && latest.current.promise) {
      return latest.current.promise;
    }
    const request = { key, promise: null };
    latest.current = request;
    setLoading(true);
    request.promise = calculateDeliveryFee(itemsRef.current)
      .then((fee) => {
        if (latest.current === request) setDeliveryFee(fee);
        return fee;
      })
      .catch((error) => {
        console.error("Error calculating delivery fee:", error);
        if (latest.current === request) setDeliveryFee(null);
        return null;
      })
      .finally(() => {
        if (latest.current === request) {
          request.promise = null;
          setLoading(false);
        }
      });
    return request.promise;
  }, [key]);

  useEffect(() => {
    if (key) loadDeliveryFee();
  }, [key, loadDeliveryFee]);

  const ensureDeliveryFee = async () => {
    const fee =
      latest.current.promise || deliveryFee === null
        ? await loadDeliveryFee()
        : deliveryFee;
    if (fee === null) {
      toast.error("Unable to calculate the delivery fee. Please try again.");
    }
    return fee;
  };

  const deliveryFeeStatus =
    deliveryFee !== null ? "ready" : loading || !key ? "loading" : "error";

  return {
    deliveryFee,
    deliveryFeeStatus,
    loadDeliveryFee,
    ensureDeliveryFee,
  };
};

export default useDeliveryFee;
